import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// One month view over meetings, routines, training and task due dates.
const KIND = {
  plan:     { colour:'#7C5CFF', label:'IT Agenda' },
  meeting:  { colour:'#4F8EF7', label:'Meeting' },
  routine:  { colour:'#2DA866', label:'Routine' },
  training: { colour:'#B26A00', label:'Training' },
  task:     { colour:'#E5484D', label:'Task due' },
  google:   { colour:'#5F6368', label:'My calendar' },
};
const CATEGORIES = ['general','audit','maintenance','rollout','review','visit','purchase','training'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

// Monday-first grid covering the whole month.
function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));      // back to Monday
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    cells.push(d);
    if (i >= 34 && d.getMonth() !== month && d.getDay() === 0) break;   // stop after the last full week
  }
  return cells;
}

export default function Calendar({ go }) {
  const { user, isAdmin } = useAuth();
  const canSeeTeam = isAdmin || user?.role === 'it_head';
  const today = new Date();
  const [ym, setYm] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [events, setEvents] = useState([]);
  const [people, setPeople] = useState([]);
  const [who, setWho] = useState('');            // '' = me, 'all' = whole team, or a user id
  const [kinds, setKinds] = useState({ plan:true, meeting:true, routine:true, training:true, task:true, google:true });
  const [sel, setSel] = useState(iso(today));
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // null | {} for new | entry for edit
  const [campuses, setCampuses] = useState([]);
  const [holidays, setHolidays] = useState({});   // 'YYYY-MM-DD' -> holiday name
  useEffect(() => { api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active))).catch(()=>{}); }, []);
  // Sunday is always a holiday; the holidays master closes the rest. Showing
  // both here means nobody plans work on a day the team is not in.
  useEffect(() => {
    api.get('/master/holidays', { params:{ year: ym.y } })
      .then(r => setHolidays(Object.fromEntries(r.data.map(h => [h.holiday_date, h.name]))))
      .catch(() => setHolidays({}));
  }, [ym.y]);

  const cells = monthGrid(ym.y, ym.m);
  const from = iso(cells[0]), to = iso(cells[cells.length - 1]);

  // My own Google Calendar entries, merged into the same grid. Only ever my
  // own — one person's calendar is never shown to another.
  const [gcal, setGcal] = useState(null);      // connection state
  const [gEvents, setGEvents] = useState([]);
  const loadGoogle = useCallback(() => {
    api.get('/calendar-sync/me').then(r => {
      setGcal(r.data);
      if (!r.data.connected) { setGEvents([]); return; }
      api.get('/calendar-sync/events', { params: { from, to } })
        .then(e => setGEvents(e.data)).catch(() => setGEvents([]));
    }).catch(() => setGcal(null));
  }, [from, to]);
  useEffect(() => { loadGoogle(); }, [loadGoogle]);

  const addToTasks = async g => {
    if (!window.confirm(`Log "${g.title}" as work you did?\n\nA ticket is created and marked completed, so it counts as your recorded work.`)) return;
    try {
      const r = await api.post(`/calendar-sync/events/${g.id}/to-task`, {});
      window.alert(`Logged as ${r.data.task_id}.`);
      loadGoogle();
    } catch (e) { window.alert(e.response?.data?.error || 'Could not log it'); }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { from, to };
    if (who === 'all') params.scope = 'all';
    else if (who) params.user_id = who;
    api.get('/calendar', { params })
      .then(r => setEvents(r.data)).catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [from, to, who]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canSeeTeam) api.get('/calendar/people').then(r => setPeople(r.data)).catch(()=>{}); }, [canSeeTeam]);

  // Google entries are shaped like portal events so the grid, the day panel and
  // the filters all treat them the same.
  const asEvents = gEvents.map(g => ({
    kind: 'google', id: g.id, on_date: g.on_date, at_time: g.all_day ? null : g.at_time,
    title: g.title,
    detail: [g.all_day ? 'All day' : (g.at_time && g.end_time ? `${g.at_time}–${g.end_time}` : g.at_time),
             g.location, g.organizer].filter(Boolean).join(' · '),
    badge: g.ticket_ref ? `Logged ${g.ticket_ref}` : 'My calendar',
    done: Boolean(g.task_id),
    google: g,
  }));
  const shown = [...events, ...asEvents].filter(e => kinds[e.kind]);
  const byDay = {};
  shown.forEach(e => (byDay[e.on_date] = byDay[e.on_date] || []).push(e));

  const move = n => setYm(s => {
    const d = new Date(s.y, s.m + n, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const dayEvents = byDay[sel] || [];

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600 }}>📅 Calendar</h2>
          <div style={{ fontSize:12, color:'var(--text2)' }}>
            Meetings, routines, training and task due dates in one place.
          </div>
        </div>
        <GoogleCalendarPanel state={gcal} reload={loadGoogle}
          events={gEvents} onAdd={addToTasks} />
        <div className="flex-gap" style={{ gap:8, flexWrap:'wrap' }}>
          {canSeeTeam && (
            <select value={who} onChange={e => setWho(e.target.value)} style={{ width:190 }}>
              <option value="">My calendar</option>
              <option value="all">Whole IT team</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => move(-1)}>‹</button>
          <button className="btn btn-secondary btn-sm"
            onClick={() => { const t = new Date(); setYm({ y:t.getFullYear(), m:t.getMonth() }); setSel(iso(t)); }}>Today</button>
          <button className="btn btn-secondary btn-sm" onClick={() => move(1)}>›</button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({ event_date: sel })}>+ Add Entry</button>
        </div>
      </div>

      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:10 }}>
        <b style={{ fontSize:15 }}>{MONTHS[ym.m]} {ym.y}</b>
        <div className="flex-gap" style={{ gap:10, flexWrap:'wrap' }}>
          {Object.entries(KIND).map(([k, v]) => (
            <label key={k} className="opt" style={{ gap:5, fontSize:12 }}>
              <input type="checkbox" checked={kinds[k]} onChange={() => setKinds(s => ({ ...s, [k]: !s[k] }))} />
              <span style={{ width:9, height:9, borderRadius:2, background:v.colour, display:'inline-block' }} />
              <span>{v.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:16 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', borderBottom:'1px solid var(--border)' }}>
          {DAYS.map(d => (
            <div key={d} style={{ padding:'7px 8px', fontSize:11, fontWeight:700, color:'var(--text2)', textAlign:'center' }}>{d}</div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)' }}>
          {cells.map(d => {
            const key = iso(d);
            const inMonth = d.getMonth() === ym.m;
            const isToday = key === iso(today);
            const list = byDay[key] || [];
            const sunday = d.getDay() === 0;
            const holiday = holidays[key];
            const off = sunday || !!holiday;          // not a working day
            return (
              <button key={key} onClick={() => setSel(key)}
                title={sunday ? 'Sunday — holiday' : holiday || undefined}
                style={{ minHeight:92, textAlign:'left', padding:'5px 6px', cursor:'pointer',
                  border:'none', borderRight:'1px solid var(--border)', borderBottom:'1px solid var(--border)',
                  background: key === sel ? 'var(--bg3)' : off ? 'rgba(229,83,61,0.06)' : 'transparent',
                  opacity: inMonth ? 1 : 0.4 }}>
                <div style={{ fontSize:11.5, fontWeight: isToday ? 700 : 500,
                  color: isToday ? '#fff' : off ? 'var(--text2)' : 'var(--text)',
                  background: isToday ? 'var(--accent)' : 'transparent',
                  borderRadius:10, width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:3 }}>
                  {d.getDate()}
                </div>
                {off && (
                  <div style={{ fontSize:9.5, color:'var(--text2)', marginBottom:2, whiteSpace:'nowrap',
                    overflow:'hidden', textOverflow:'ellipsis' }}>
                    {sunday ? 'Sunday' : holiday}
                  </div>
                )}
                {list.slice(0, 3).map((e, i) => (
                  <div key={i} title={`${e.title} — ${e.detail}`}
                    style={{ fontSize:10, marginBottom:2, padding:'1px 4px', borderRadius:3,
                      background:`${KIND[e.kind].colour}22`, color:KIND[e.kind].colour,
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                      textDecoration: e.done ? 'line-through' : 'none' }}>
                    {e.at_time ? `${e.at_time} ` : ''}{e.title}
                  </div>
                ))}
                {list.length > 3 && <div style={{ fontSize:10, color:'var(--text2)' }}>+{list.length - 3} more</div>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <b style={{ fontSize:14 }}>
            {new Date(sel + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
          </b>
          {loading && <span style={{ fontSize:12, color:'var(--text2)' }}>Loading…</span>}
        </div>
        {!dayEvents.length && <div style={{ fontSize:13, color:'var(--text2)' }}>Nothing scheduled.</div>}
        {dayEvents.map((e, i) => (
          <button key={i} onClick={() => e.editable
            ? api.get(`/calendar/events/${e.id}`).then(r => setEditing(r.data)).catch(()=>{})
            : e.link_page && go?.(e.link_page, e.link_arg)}
            style={{ display:'block', width:'100%', textAlign:'left', border:'none', cursor: e.link_page ? 'pointer' : 'default',
              padding:'8px 10px', marginBottom:6, borderRadius:'var(--radius)',
              background:'var(--bg3)', borderLeft:`3px solid ${KIND[e.kind].colour}` }}>
            <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
              {e.at_time && <span style={{ fontSize:12, fontWeight:700 }}>{e.at_time}</span>}
              <span style={{ fontSize:13, fontWeight:600, textDecoration: e.done ? 'line-through' : 'none' }}>{e.title}</span>
              <span className="badge" style={{ fontSize:9.5, background:`${KIND[e.kind].colour}22`, color:KIND[e.kind].colour }}>{e.badge}</span>
            </div>
            <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:2 }}>{e.detail}</div>
            {/* A calendar entry says what was planned. The person decides
                whether it was IT work worth recording — then the ticket, not
                the calendar, is the record. */}
            {e.kind === 'google' && (
              <div style={{ marginTop:6 }} onClick={ev => ev.stopPropagation()}>
                {e.google.task_id ? (
                  <span style={{ fontSize:11.5, color:'var(--green)' }}>
                    ✓ Logged as {e.google.ticket_ref}
                  </span>
                ) : (
                  <span
                    role="button" tabIndex={0}
                    className="btn btn-secondary btn-sm"
                    onClick={() => addToTasks(e.google)}
                    onKeyDown={ev => ev.key === 'Enter' && addToTasks(e.google)}
                  >+ Add to my tasks</span>
                )}
              </div>
            )}
          </button>
        ))}
      </div>

      {editing && <EntryModal entry={editing} campuses={campuses}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// ── Connect my Google Calendar ──────────────────────────────────────────────
// Each person connects their own work calendar. What comes back is theirs
// alone — nobody sees anyone else's entries.
function GoogleCalendarPanel({ state, reload, events = [], onAdd }) {
  const [busy, setBusy] = useState('');
  if (!state || !state.available) return null;    // no client configured yet

  const connect = async () => {
    setBusy('connect');
    try {
      const r = await api.post('/calendar-sync/connect');
      // A popup, so the portal stays open behind the Google consent screen.
      const w = window.open(r.data.url, 'gcal', 'width=520,height=640');
      const onMsg = e => {
        if (e.data === 'calendar-connected') {
          window.removeEventListener('message', onMsg); reload();
        }
      };
      window.addEventListener('message', onMsg);
      // Some browsers block the postMessage; refresh when the popup closes too.
      const t = setInterval(() => { if (!w || w.closed) { clearInterval(t); reload(); } }, 1200);
    } catch (e) { window.alert(e.response?.data?.error || 'Could not start the connection'); }
    finally { setBusy(''); }
  };

  const sync = async () => {
    setBusy('sync');
    try { const r = await api.post('/calendar-sync/sync'); window.alert(`${r.data.events} entr(ies) synced.`); reload(); }
    catch (e) { window.alert(e.response?.data?.error || 'Sync failed'); }
    finally { setBusy(''); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your Google Calendar?\n\nSynced entries are removed from the portal. Anything you already logged as a ticket stays.')) return;
    try { await api.post('/calendar-sync/disconnect'); reload(); } catch (e) {}
  };

  if (!state.connected) return (
    <button className="btn btn-secondary btn-sm" disabled={busy === 'connect'} onClick={connect}>
      {busy === 'connect' ? 'Opening Google…' : '🔗 Connect my calendar'}
    </button>
  );

  // Entries worth logging: mine, not already a ticket, and not still in the
  // future — a webinar next Tuesday is not work done yet. Surfaced here as
  // well as in the day panel, because hunting day by day for them is no way
  // to find out what you have not recorded.
  const today = new Date().toLocaleDateString('en-CA');   // YYYY-MM-DD, local
  const pending = events.filter(g => !g.task_id && g.on_date <= today);

  return (
    <div>
    <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
      <span style={{ fontSize:11.5, color:'var(--text2)' }}>
        {state.connected_email}
        {state.last_synced_at && ` · synced ${new Date(state.last_synced_at).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}`}
      </span>
      <button className="btn btn-secondary btn-sm" disabled={busy === 'sync'} onClick={sync}>
        {busy === 'sync' ? 'Syncing…' : '↻ Sync now'}
      </button>
      <button className="btn btn-secondary btn-sm" onClick={disconnect}>Disconnect</button>
      {state.last_error && (
        <span style={{ fontSize:11.5, color:'var(--red)' }} title={state.last_error}>sync error</span>
      )}
    </div>

    {pending.length > 0 && (
      <div style={{ marginTop:8, padding:'8px 10px', borderRadius:'var(--radius)',
                    background:'var(--bg3)', maxWidth:560 }}>
        <div style={{ fontSize:11.5, color:'var(--text2)', marginBottom:6 }}>
          {pending.length} calendar {pending.length === 1 ? 'entry is' : 'entries are'} not
          recorded as work yet — log the ones that were IT work:
        </div>
        {pending.slice(0, 6).map(g => (
          <div key={g.id} className="flex-gap"
               style={{ gap:8, alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
            <span style={{ fontSize:12.5 }}>
              <b>{g.on_date}</b> · {g.title}
            </span>
            <button className="btn btn-secondary btn-sm" onClick={() => onAdd && onAdd(g)}>
              + Add to my tasks
            </button>
          </div>
        ))}
        {pending.length > 6 && (
          <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:4 }}>
            …and {pending.length - 6} more — open the day in the calendar below.
          </div>
        )}
      </div>
    )}
    </div>
  );
}

// Plan an entry for the year: a single day or a span, timed or all-day.
function EntryModal({ entry, campuses, onClose, onSaved }) {
  const isNew = !entry.id;
  const [f, setF] = useState({
    title: entry.title || '', description: entry.description || '',
    category: entry.category || 'general', event_date: entry.event_date || '',
    end_date: entry.end_date || '', start_time: entry.start_time || '',
    end_time: entry.end_time || '', campus_id: entry.campus_id || '',
  });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');

  const save = async () => {
    if (!f.title.trim() || !f.event_date) { setErr('Title and date are required'); return; }
    setBusy(true); setErr('');
    const body = { ...f, campus_id: f.campus_id || null, end_date: f.end_date || null,
      start_time: f.start_time || null, end_time: f.end_time || null };
    try {
      if (isNew) await api.post('/calendar/events', body);
      else await api.put(`/calendar/events/${entry.id}`, body);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove "${f.title}" from the calendar?`)) return;
    await api.delete(`/calendar/events/${entry.id}`).catch(()=>{});
    onSaved();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-header"><span className="modal-title">{isNew ? 'Add Calendar Entry' : 'Edit Entry'}</span>
          <button className="modal-close" onClick={onClose}>×</button></div>

        <div className="form-group"><label>Title *</label>
          <input value={f.title} onChange={e => setF(s => ({ ...s, title:e.target.value }))}
            placeholder="e.g. Annual server room audit" /></div>

        <div className="form-row">
          <div className="form-group"><label>Date *</label>
            <input type="date" value={f.event_date} onChange={e => setF(s => ({ ...s, event_date:e.target.value }))} /></div>
          <div className="form-group"><label>End date <span style={{ fontSize:11, color:'var(--text2)' }}>— for a span</span></label>
            <input type="date" value={f.end_date} onChange={e => setF(s => ({ ...s, end_date:e.target.value }))} /></div>
        </div>

        <div className="form-row">
          <div className="form-group"><label>Start time <span style={{ fontSize:11, color:'var(--text2)' }}>— blank = all day</span></label>
            <input type="time" value={f.start_time} onChange={e => setF(s => ({ ...s, start_time:e.target.value }))} /></div>
          <div className="form-group"><label>End time</label>
            <input type="time" value={f.end_time} onChange={e => setF(s => ({ ...s, end_time:e.target.value }))} /></div>
        </div>

        <div className="form-row">
          <div className="form-group"><label>Category</label>
            <select value={f.category} onChange={e => setF(s => ({ ...s, category:e.target.value }))}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select></div>
          <div className="form-group"><label>Campus</label>
            <select value={f.campus_id} onChange={e => setF(s => ({ ...s, campus_id:e.target.value }))}>
              <option value="">All campuses</option>
              {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
        </div>

        <div className="form-group"><label>Notes</label>
          <textarea rows={3} value={f.description} onChange={e => setF(s => ({ ...s, description:e.target.value }))} /></div>

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer" style={{ justifyContent:'space-between' }}>
          {!isNew ? <button className="btn btn-danger btn-sm" onClick={del}>Remove</button> : <span />}
          <div className="flex-gap" style={{ gap:8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
